/**
 * Workflow API Service
 * Mirrors: app/Http/Controllers/Api/WorkflowController.php
 */

import { BaseApiClient } from './base.api'
import type {
  Workflow,
  WorkflowRun,
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  ExecuteWorkflowRequest,
  QueueBatchRequest,
  PaginatedResponse,
} from '@/types'

export class WorkflowApi extends BaseApiClient {
  /**
   * List workflows with filtering and pagination
   * GET /workflows
   */
  async list(params?: {
    is_active?: boolean
    content_type?: string
    sort_by?: string
    sort_order?: 'asc' | 'desc'
    per_page?: number
  }): Promise<{ data: Workflow[] }> {
    return super.get('/workflows', { params })
  }

  /**
   * Get single workflow with steps
   * GET /workflows/{id}
   */
  async getWorkflow(id: number): Promise<{ workflow: Workflow }> {
    return super.get(`/workflows/${id}`)
  }

  /**
   * Create new workflow
   * POST /workflows
   */
  async create(data: CreateWorkflowRequest): Promise<{ workflow: Workflow }> {
    return this.post('/workflows', data)
  }

  /**
   * Update workflow
   * PUT /workflows/{id}
   */
  async update(id: number, data: UpdateWorkflowRequest): Promise<{ workflow: Workflow }> {
    return this.put(`/workflows/${id}`, data)
  }

  /**
   * Delete workflow
   * DELETE /workflows/{id}
   */
  async deleteWorkflow(id: number): Promise<{ message: string }> {
    return super.delete(`/workflows/${id}`)
  }

  /**
   * Duplicate workflow
   * POST /workflows/{id}/duplicate
   */
  async duplicate(id: number, name: string): Promise<{ workflow: Workflow }> {
    return this.post(`/workflows/${id}/duplicate`, { name })
  }

  /**
   * Validate workflow configuration
   * POST /workflows/{id}/validate
   */
  async validate(id: number): Promise<{ valid: boolean; errors?: string[] }> {
    return this.post(`/workflows/${id}/validate`)
  }

  /**
   * Get execution plan
   * GET /workflows/{id}/execution-plan
   */
  async getExecutionPlan(id: number): Promise<{ plan: any[] }> {
    return super.get(`/workflows/${id}/execution-plan`)
  }

  /**
   * Execute workflow immediately
   * POST /workflows/{id}/execute
   */
  async execute(id: number, data: ExecuteWorkflowRequest): Promise<{ run: WorkflowRun }> {
    return this.post(`/workflows/${id}/execute`, data)
  }

  /**
   * Queue single workflow execution
   * POST /workflows/{id}/queue
   */
  async queueExecution(
    id: number,
    data: { input_data: Record<string, unknown>; metadata?: unknown }
  ): Promise<{ message: string; job_id: string }> {
    return this.post(`/workflows/${id}/queue`, data)
  }

  /**
   * Queue batch execution with topics
   * POST /workflows/{id}/queue-batch
   */
  async queueBatch(id: number, data: QueueBatchRequest): Promise<{ message: string; queued: number }> {
    return this.post(`/workflows/${id}/queue-batch`, data)
  }

  /**
   * Queue execution for existing content items
   * POST /workflows/{id}/queue-content-items
   */
  async queueContentItems(id: number, data: { content_item_ids: number[] }): Promise<{ message: string }> {
    return this.post(`/workflows/${id}/queue-content-items`, data)
  }

  /**
   * Get workflow run history
   * GET /workflows/{id}/runs
   */
  async getRuns(id: number, params?: { per_page?: number }): Promise<PaginatedResponse<WorkflowRun>> {
    return super.get(`/workflows/${id}/runs`, { params })
  }

  /**
   * Get specific run details
   * GET /workflows/{workflowId}/runs/{runId}
   */
  async getRun(workflowId: number, runId: number): Promise<{ run: WorkflowRun }> {
    return super.get(`/workflows/${workflowId}/runs/${runId}`)
  }

  // Workflow Steps Management

  /**
   * Add step to workflow
   * POST /workflows/{workflowId}/steps
   */
  async addStep(
    workflowId: number,
    data: {
      name: string
      description?: string
      prompt_id: number
      output_variable: string
      variable_mappings?: Record<string, string>
      order: number
    }
  ): Promise<{ step: any }> {
    return this.post(`/workflows/${workflowId}/steps`, data)
  }

  /**
   * Update workflow step
   * PUT /workflows/{workflowId}/steps/{stepId}
   */
  async updateStep(workflowId: number, stepId: number, data: any): Promise<{ step: any }> {
    return this.put(`/workflows/${workflowId}/steps/${stepId}`, data)
  }

  /**
   * Delete workflow step
   * DELETE /workflows/{workflowId}/steps/{stepId}
   */
  async deleteStep(workflowId: number, stepId: number): Promise<{ message: string }> {
    return super.delete(`/workflows/${workflowId}/steps/${stepId}`)
  }

  /**
   * Reorder workflow steps
   * POST /workflows/{workflowId}/steps/reorder
   */
  async reorderSteps(workflowId: number, data: { step_order: number[] }): Promise<{ message: string }> {
    return this.post(`/workflows/${workflowId}/steps/reorder`, data)
  }
}

// Export singleton instance
export const workflowApi = new WorkflowApi()
